import React, { useState, useEffect } from 'react';
import { InformeResponse } from '@/types';
import { informeService } from '@/services/informeService';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { TextArea } from './TextArea';
import { Card } from './Card';
import { useAuth } from '@/contexts/AuthContext';
import { 
  getNivelLabel, 
  getPosicionLabel, 
  getLateralidadLabel,
  getLetraEquipoLabel,
  getCompeticionLabel,
  getCategoriaLabel,
  getCategoriaFuturaLabel,
  getCategoriasFuturas,
  getNiveles,
  getPosiciones,
  getLateralidades,
  getLetrasEquipo,
  getCompeticiones,
  getCategorias,
  getAñosDisponibles,
  Nivel,
  Posicion,
  Lateralidad,
  LetraEquipo,
  Competicion,
  Categoria,
  CategoriaFutura
} from '@/types/enums';
import { BarChart3 } from 'lucide-react';
import { optimizeImageForUpload } from '@/utils/imageUpload';

interface DetalleInformeProps {
  informe: InformeResponse;
  onVolver: () => void;
  onActualizar: (id: number, data: Partial<InformeResponse>) => Promise<InformeResponse>;
}

/**
 * Componente para mostrar y editar los detalles de un informe
 * @param informe - Informe a mostrar/editar
 * @param onVolver - Función para volver a la lista
 * @param onActualizar - Función para actualizar el informe
 */
export const DetalleInforme: React.FC<DetalleInformeProps> = ({
  informe,
  onVolver,
  onActualizar
}) => {
  const { user } = useAuth();
  const [modoEdicion, setModoEdicion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [datosEditados, setDatosEditados] = useState(informe);
  const [mostrarFoto, setMostrarFoto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [cargandoFoto, setCargandoFoto] = useState(false);
  const [nuevaFoto, setNuevaFoto] = useState<File | null>(null);
  const [mostrarCambiarFoto, setMostrarCambiarFoto] = useState(false);

  // Actualizar datos cuando cambie el informe
  useEffect(() => {
    setDatosEditados(informe);
  }, [informe]);

  // Cleanup de la URL del blob
  useEffect(() => {
    return () => {
      if (fotoUrl) {
        URL.revokeObjectURL(fotoUrl);
      }
    };
  }, [fotoUrl]);

  /**
   * Maneja el cambio de modo (visualización/edición)
   */
  const toggleModo = () => {
    if (modoEdicion) {
      // Si estamos saliendo del modo edición, restaurar datos originales
      setDatosEditados(informe);
      setNuevaFoto(null);
    }
    setModoEdicion(!modoEdicion);
  };

  const [errorMessage, setErrorMessage] = useState<string>('');

  /**
   * Maneja el guardado de cambios
   */
  const handleGuardar = async () => {
    try {
      setLoading(true);
      setErrorMessage(''); // Limpiar error anterior
      
      // Llamar a la función onActualizar del componente padre
      // para que se actualice la lista correctamente
      const informeActualizado = await onActualizar(informe.id, {
        ...datosEditados,
        // Si hay nueva foto, incluirla en los datos
        ...(nuevaFoto && { foto: nuevaFoto })
      });
      
      // Actualizar los datos locales con la respuesta del servidor
      setDatosEditados(informeActualizado);
      setModoEdicion(false);
      setNuevaFoto(null);
    } catch (error) {
      console.error('Error al guardar:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error al actualizar el informe';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja los cambios en los campos del formulario
   */
  const handleCambio = (campo: keyof InformeResponse, valor: any) => {
    setDatosEditados(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  /**
   * Carga la foto del informe
   */
  const cargarFoto = async () => {
    if (!informe.tieneFoto) return;
    
    setCargandoFoto(true);
    try {
      const url = await informeService.obtenerFotoBlob(informe.id);
      setFotoUrl(url);
    } catch (error) {
      console.error('Error cargando foto:', error);
    } finally {
      setCargandoFoto(false);
    }
  };

  /**
   * Muestra el modal de la foto
   */
  const handleVerFoto = () => {
    if (!fotoUrl) {
      cargarFoto();
    }
    setMostrarFoto(true);
  };

  /**
   * Cierra el modal de la foto
   */
  const handleCerrarFoto = () => {
    setMostrarFoto(false);
  };

  /**
   * Maneja la selección de nueva foto
   */
  const handleSeleccionarFoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar que sea una imagen
      if (file.type.startsWith('image/')) {
        const optimizedFile = await optimizeImageForUpload(file);
        setNuevaFoto(optimizedFile);
        setMostrarCambiarFoto(false);
      } else {
        alert('Por favor selecciona un archivo de imagen válido');
      }
    }
    
    // Limpiar el input para permitir seleccionar el mismo archivo otra vez
    event.target.value = '';
  };

  /**
   * Elimina la nueva foto seleccionada
   */
  const handleEliminarNuevaFoto = () => {
    setNuevaFoto(null);
  };

  /**
   * Formatea la fecha de creación
   */
  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header mejorado */}
      <div className="space-y-4">
        {/* Título y fecha */}
        <div>
          <h2 className="text-2xl font-bold text-secondary-50 mb-2">
            {modoEdicion ? 'Editar Informe' : 'Detalle del Informe'}
          </h2>
          <p className="text-sm text-secondary-400">
            Creado: {formatearFecha(informe.createdAt)}
          </p>
        </div>

        {/* Botones de acción reorganizados */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Fila superior: Botón volver y cancelar */}
          <div className="flex justify-between w-full">
            <Button
              onClick={onVolver}
              variant="secondary"
              size="sm"
              className="flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver
            </Button>
            
            {modoEdicion && (
              <Button
                onClick={toggleModo}
                variant="secondary"
                size="sm"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancelar
              </Button>
            )}
          </div>

          {/* Fila inferior: Botones de foto y edición */}
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            {/* Botones de foto */}
            {informe.tieneFoto && (
              <Button
                onClick={handleVerFoto}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Ver foto
              </Button>
            )}

            {modoEdicion && (
              <Button
                onClick={() => setMostrarCambiarFoto(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {informe.tieneFoto ? 'Cambiar foto' : 'Agregar foto'}
              </Button>
            )}

            {!modoEdicion && (
              <Button
                onClick={toggleModo}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar
              </Button>
            )}
          </div>
        </div>

        {/* Indicador de nueva foto seleccionada */}
        {nuevaFoto && (
          <div className="mt-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/30 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-green-400 font-medium text-sm">Nueva foto seleccionada</p>
                  <p className="text-green-300 text-xs">{nuevaFoto.name}</p>
                  <p className="text-green-300 text-xs">{(nuevaFoto.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <Button
                onClick={handleEliminarNuevaFoto}
                variant="secondary"
                size="sm"
                className="text-red-400 hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Resto del contenido del informe */}
      <div className="space-y-4">
        {/* Información del jugador */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-secondary-50 mb-4">
            Información del Jugador
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Nombre completo */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Nombre Completo
              </label>
              {modoEdicion ? (
                <Input
                  value={datosEditados.nombreCompleto}
                  onChange={(e) => handleCambio('nombreCompleto', e.target.value)}
                  placeholder="Nombre completo del jugador"
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {datosEditados.nombreCompleto}
                </div>
              )}
            </div>

            {/* Año */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Año de Nacimiento
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.año.toString()}
                  onChange={(e) => handleCambio('año', parseInt(e.target.value))}
                  options={getAñosDisponibles(user?.seccion).map(año => ({
                    value: año.toString(),
                    label: año.toString()
                  }))}
                  placeholder="Selecciona un año"
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {datosEditados.año}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Información del equipo */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-secondary-50 mb-4">
            Información del Equipo
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Nombre del equipo */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Nombre del Equipo
              </label>
              {modoEdicion ? (
                <Input
                  value={datosEditados.nombreEquipo}
                  onChange={(e) => handleCambio('nombreEquipo', e.target.value)}
                  placeholder="Nombre del equipo"
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {datosEditados.nombreEquipo}
                </div>
              )}
            </div>

            {/* Letra del equipo */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Letra del Equipo
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.letraEquipo}
                  onChange={(e) => handleCambio('letraEquipo', e.target.value as LetraEquipo)}
                  options={getLetrasEquipo().map(letra => ({
                    value: letra,
                    label: getLetraEquipoLabel(letra)
                  }))}
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {getLetraEquipoLabel(datosEditados.letraEquipo)}
                </div>
              )}
            </div>

            {/* Competición - Solo mostrar si no hay partido asignado */}
            {!informe.partidoId && (
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1">
                  Competición
                </label>
                {modoEdicion ? (
                  <Select
                    value={datosEditados.competicion}
                    onChange={(e) => handleCambio('competicion', e.target.value as Competicion)}
                    options={getCompeticiones(user?.seccion).map(competicion => ({
                      value: competicion,
                      label: getCompeticionLabel(competicion)
                    }))}
                    placeholder="Selecciona una competición"
                  />
                ) : (
                  <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                    {getCompeticionLabel(datosEditados.competicion)}
                  </div>
                )}
              </div>
            )}

            {/* Categoría - Solo mostrar si no hay partido asignado */}
            {!informe.partidoId && (
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1">
                  Categoría
                </label>
                {modoEdicion ? (
                  <Select
                    value={datosEditados.categoria}
                    onChange={(e) => handleCambio('categoria', e.target.value as Categoria)}
                    options={getCategorias(user?.seccion).map(categoria => ({
                      value: categoria,
                      label: getCategoriaLabel(categoria)
                    }))}
                    placeholder="Selecciona una categoría"
                  />
                ) : (
                  <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                    {getCategoriaLabel(datosEditados.categoria)}
                  </div>
                )}
              </div>
            )}

            {/* Grupo - Solo mostrar si no hay partido asignado */}
            {!informe.partidoId && (
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1">
                  Grupo
                </label>
                {modoEdicion ? (
                  <Input
                    type="number"
                    value={datosEditados.grupo?.toString() || ''}
                    onChange={(e) => handleCambio('grupo', parseInt(e.target.value) || 0)}
                    placeholder="Número de grupo"
                    min="1"
                    max="100"
                  />
                ) : (
                  <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                    {datosEditados.grupo}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Características técnicas */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-secondary-50 mb-4">
            Características Técnicas
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Dorsal */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Dorsal
              </label>
              {modoEdicion ? (
                <Input
                  type="number"
                  value={datosEditados.dorsal?.toString() || ''}
                  onChange={(e) => handleCambio('dorsal', parseInt(e.target.value) || undefined)}
                  placeholder="Número de dorsal (1-99)"
                  min="1"
                  max="99"
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {datosEditados.dorsal || 'Sin asignar'}
                </div>
              )}
            </div>

            {/* Categoría Futura */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Categoría Futura
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.categoriaFutura || ''}
                  onChange={(e) => handleCambio('categoriaFutura', e.target.value as CategoriaFutura)}
                  options={getCategoriasFuturas(user?.seccion).map(categoria => ({
                    value: categoria,
                    label: getCategoriaFuturaLabel(categoria)
                  }))}
                  placeholder="Selecciona una categoría futura"
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {datosEditados.categoriaFutura ? getCategoriaFuturaLabel(datosEditados.categoriaFutura) : 'Sin asignar'}
                </div>
              )}
            </div>

            {/* Posición */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Posición
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.posicion}
                  onChange={(e) => handleCambio('posicion', e.target.value as Posicion)}
                  options={getPosiciones(user?.seccion).map(posicion => ({
                    value: posicion,
                    label: getPosicionLabel(posicion)
                  }))}
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {getPosicionLabel(datosEditados.posicion)}
                </div>
              )}
            </div>

            {/* Lateralidad */}
            <div>
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Lateralidad
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.lateralidad}
                  onChange={(e) => handleCambio('lateralidad', e.target.value as Lateralidad)}
                  options={getLateralidades().map(lateralidad => ({
                    value: lateralidad,
                    label: getLateralidadLabel(lateralidad)
                  }))}
                />
              ) : (
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {getLateralidadLabel(informe.lateralidad)}
                </div>
              )}
            </div>

            {/* Nivel */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-secondary-400 mb-1">
                Nivel
              </label>
              {modoEdicion ? (
                <Select
                  value={datosEditados.nivel}
                  onChange={(e) => handleCambio('nivel', e.target.value as Nivel)}
                  options={getNiveles().map(nivel => ({
                    value: nivel,
                    label: getNivelLabel(nivel)
                  }))}
                />
              ) : (
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
                  datosEditados.nivel === 'FICHAR' ? 'bg-green-500/20 text-green-400' :
                  datosEditados.nivel === 'INTERESANTE' ? 'bg-blue-500/20 text-blue-400' :
                  datosEditados.nivel === 'SEGUIR' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {getNivelLabel(datosEditados.nivel)}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Habilidades */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-secondary-50" />
            <h3 className="text-lg font-semibold text-secondary-50">
              Valoración de Habilidades
            </h3>
          </div>
          
          <div className="space-y-4">
            {['potenciaFisica', 'velocidad', 'tomaDecision', 'chute', 'tecnicaIndividual'].map((skill) => (
              <div key={skill} className="space-y-1">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-secondary-300 capitalize">
                    {skill.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                    (datosEditados[skill as keyof InformeResponse] as number) >= 3 ? 'bg-green-500/20 text-green-400' :
                    (datosEditados[skill as keyof InformeResponse] as number) === 2 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {datosEditados[skill as keyof InformeResponse] as number || 2}
                  </span>
                </div>
                
                {modoEdicion ? (
                  <>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4].map((valor) => (
                        <button
                          key={valor}
                          type="button"
                          onClick={() => handleCambio(skill as keyof InformeResponse, valor)}
                          className={`flex-1 h-9 rounded-lg font-bold transition-all text-sm ${
                            (datosEditados[skill as keyof InformeResponse] as number || 2) === valor
                              ? valor >= 3 
                                ? 'bg-green-500 text-white' 
                                : valor === 2 
                                  ? 'bg-yellow-500 text-white' 
                                  : 'bg-red-500 text-white'
                              : 'bg-secondary-700 text-secondary-400 hover:bg-secondary-600'
                          }`}
                        >
                          {valor}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-secondary-500 mt-1 px-1">
                      <span>Bajo</span>
                      <span>Alto</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full bg-secondary-700 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${
                        (datosEditados[skill as keyof InformeResponse] as number) >= 3 ? 'bg-green-500' :
                        (datosEditados[skill as keyof InformeResponse] as number) === 2 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${((datosEditados[skill as keyof InformeResponse] as number || 2) / 4) * 100}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Observaciones */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-secondary-50 mb-4">
            Observaciones
          </h3>
          
          {modoEdicion ? (
            <TextArea
              value={datosEditados.observaciones || ''}
              onChange={(e) => handleCambio('observaciones', e.target.value)}
              placeholder="Observaciones sobre el jugador..."
              rows={4}
            />
          ) : (
            <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm min-h-[100px]">
              {datosEditados.observaciones || 'Sin observaciones'}
            </div>
          )}
        </Card>

        {/* Información adicional */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-secondary-50 mb-4">
            Información Adicional
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-secondary-400">Autor: </span>
              <span className="text-secondary-300">{informe.autor}</span>
            </div>
            <div>
              <span className="text-secondary-400">Partido: </span>
              <span className="text-secondary-300">{informe.nombrePartido}</span>
            </div>
            <div>
              <span className="text-secondary-400">Categoría: </span>
              <span className="text-secondary-300">{informe.categoria}</span>
            </div>
            <div>
              <span className="text-secondary-400">Grupo: </span>
              <span className="text-secondary-300">Grupo {informe.grupo}</span>
            </div>
            {informe.updatedAt !== informe.createdAt && (
              <div className="sm:col-span-2">
                <span className="text-secondary-400">Última actualización: </span>
                <span className="text-secondary-300">{formatearFecha(informe.updatedAt)}</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Botón de guardar - Abajo del todo */}
      {modoEdicion && (
        <div className="mt-6 flex flex-col items-center">
          {/* Mensaje de error */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm max-w-md text-center">
              {errorMessage}
            </div>
          )}
          
          <Button
            onClick={handleGuardar}
            variant="primary"
            size="lg"
            disabled={loading}
            className="flex items-center gap-2 px-8"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Guardando...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar
              </>
            )}
          </Button>
        </div>
      )}

      {/* Modal para mostrar la foto */}
      {mostrarFoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary-800 rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="flex justify-between items-center p-4 border-b border-secondary-700">
              <h3 className="text-lg font-semibold text-secondary-50">Foto de {informe.nombreCompleto}</h3>
              <button
                onClick={handleCerrarFoto}
                className="text-secondary-400 hover:text-secondary-200 text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              {cargandoFoto ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-2 text-secondary-300">Cargando foto...</span>
                </div>
              ) : fotoUrl ? (
                <img
                  src={fotoUrl}
                  alt={`Foto de ${informe.nombreCompleto}`}
                  className="max-w-full max-h-96 object-contain mx-auto rounded-lg"
                />
              ) : (
                <div className="text-center text-secondary-400 h-64 flex items-center justify-center">
                  Error al cargar la foto
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para cambiar foto */}
      {mostrarCambiarFoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary-800 rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b border-secondary-700">
              <h3 className="text-lg font-semibold text-secondary-50">
                {informe.tieneFoto ? 'Cambiar Foto' : 'Agregar Foto'}
              </h3>
              <button
                onClick={() => setMostrarCambiarFoto(false)}
                className="text-secondary-400 hover:text-secondary-200 text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <p className="text-secondary-300 text-sm">
                  Selecciona una nueva imagen para el jugador. Se aceptan archivos JPG, PNG y GIF.
                </p>
                
                <div className="border-2 border-dashed border-secondary-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSeleccionarFoto}
                    className="hidden"
                    id="foto-input"
                  />
                  <label
                    htmlFor="foto-input"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <svg className="w-12 h-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-secondary-300 font-medium">Haz clic para seleccionar</span>
                    <span className="text-secondary-400 text-xs">o arrastra y suelta una imagen aquí</span>
                  </label>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={() => setMostrarCambiarFoto(false)}
                    variant="secondary"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
